import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useImmer } from "use-immer";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import styles from "./SearchKeywords.module.scss";
import { Button } from "src/components";
import { useAppDispatch } from "src/store";
import { useModal, usePreviousState, useLatestKeywordsDate } from "src/hooks";
import { removeExtraSpaces, showToastNotification } from "src/utils";
import { useSearchStatusObserver } from "src/pages/Trackers/CreateTrackers/components/SelectSearchesSection/hooks";
import {
  getSearchKeywords,
  getSearchSelectedKeywords,
} from "src/store/searches/searchesApi";
import {
  selectTrackersWithSearchId,
  selectSearchConfigurationById,
  selectTrackersCollectionsWithSearchId,
  selectUser,
  selectUserPermissions,
  selectSearchesByTrackerId,
  selectTrackerById,
} from "src/store/selectors";
import {
  saveTemporaryRelatedKeywords,
  clearTemporaryRelatedKeywords,
} from "src/store/searchesConfiguration/searchesConfigurationSlice";
import {
  selectTemporaryRelatedKeywords,
  hasTemporaryRelatedKeywords,
} from "src/store/searchesConfiguration/searchesConfigurationSelector";
import {
  updateSearch,
  updateTrackers,
  updateSearchConfiguration,
  updateTrackersCollections,
  createSearch,
} from "src/store/actions";
import { RecalculationConfirmModal } from "src/features/Modals/RecalculationConfirmModal/RecalculationConfirmModal";
import { KeywordsTable } from "../KeywordsTable/KeywordsTable";
import type { Keyword, SelectStatus } from "../KeywordsTable/types";

// Inner imports
import { KeywordsControl, SelectedSearch } from "./components";
import {
  getGroupedKeywords,
  getDuplicatedKeywords,
  getExactMatchKeywordRegExp,
} from "./utils";
import { useTrackerSearches } from "src/pages/Trackers/hooks";
import { generateDocId } from "src/store/utils";
import { SortType } from "rsuite/esm/Table";
import { KeywordsTab } from "./types";

/**
 * Detect keyword changes by comparing old and new keyword arrays
 */
function detectKeywordChanges(
  oldKeywords: string[],
  newKeywords: string[],
): { added: string[]; removed: string[] } {
  const oldSet = new Set(oldKeywords.map(k => k.toLowerCase()));
  const newSet = new Set(newKeywords.map(k => k.toLowerCase()));

  const added = newKeywords.filter((k) => !oldSet.has(k.toLowerCase()));
  const removed = oldKeywords.filter((k) => !newSet.has(k.toLowerCase()));

  return { added, removed };
}

type Props = {
  search: Search.CreationData & { authorId?: string };
  keywordsData?: Search.KeywordsData;
  closeSidebarHandler: () => void;
  trackerId?: string;
  selectSearchHandler?: (search: Search.CreationData) => void;
};

export const SearchKeywords: FC<Props> = ({
  search,
  closeSidebarHandler,
  keywordsData: defaultKeywordsData,
  trackerId,
  selectSearchHandler,
}) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const { setModal, closeModal } = useModal();

  const trackersCollections = useSelector((state: Store.RootState) =>
    selectTrackersCollectionsWithSearchId(state, search.id),
  );

  const trackers = useSelector((state: Store.RootState) =>
    selectTrackersWithSearchId(state, search.id),
  );

  const user = useSelector(selectUser);

  const { isUserCompanyOwnerOrAdmin } = useSelector(selectUserPermissions);

  const isUserSearchOwner = useMemo<boolean>(
    () => user.id === search.authorId,
    [search.authorId, user.id],
  );

  const allowChangeTracker = useMemo<boolean>(
    () => isUserSearchOwner || isUserCompanyOwnerOrAdmin,
    [isUserSearchOwner, isUserCompanyOwnerOrAdmin],
  );

  const searchConfiguration = useSelector((state: Store.RootState) =>
    selectSearchConfigurationById(state, search.id),
  );

  const temporaryRelatedKeywords = useSelector((state: Store.RootState) =>
    selectTemporaryRelatedKeywords(state, search.id),
  );

  const hasTemporaryKeywords = useSelector((state: Store.RootState) =>
    hasTemporaryRelatedKeywords(state, search.id),
  );

  const latestKeywordsDate = useLatestKeywordsDate([search.keywordsDataSource]);

  const defaultSelectedKeywords = useMemo<Record<Keyword, true>>(() => {
    const searchSelectedKeywords = searchConfiguration?.keywords || [];

    const keywords = new Map<Keyword, true>();

    for (const keyword of searchSelectedKeywords) {
      const normalizedKeyword = keyword.toLowerCase();
      keywords.set(normalizedKeyword, true);
    }
    return Object.fromEntries(keywords);
  }, [searchConfiguration?.keywords]);

  const [committedSelectedKeywords, setCommittedSelectedKeywords] = useState<Record<Keyword, true>>(defaultSelectedKeywords);

  const { searchStatus, isObserverSet } = useSearchStatusObserver(search);

  const [selectedKeywords, setSelectedKeywords] = useState<
    Record<Keyword, true>
  >(defaultSelectedKeywords);

  const [autoSelectedKeywords, setAutoSelectedKeywords] =
    useState<Nullable<Record<Keyword, true>>>(null);

  const [activeTab, setActiveTab] = useState<KeywordsTab>("all");

  const [keywordsSearch, setKeywordsSearch] = useState<string>("");

  const [keywordFilterMode, setKeywordFilterModeState] = useState<"exact" | "all">("exact");
  const [isFilterModeInitialized, setIsFilterModeInitialized] = useState(false);

  const exactMatchRegex = useMemo(
    () => getExactMatchKeywordRegExp(search),
    [search]
  );

  useEffect(() => {
    if (isFilterModeInitialized) return;
    
    const selectedKeywordsList = searchConfiguration?.keywords || [];
    if (!selectedKeywordsList.length) {
      setIsFilterModeInitialized(true);
      return;
    }

    const hasRelatedKeywordsSelected = selectedKeywordsList.some(
      (keyword) => !exactMatchRegex.test(keyword.toLowerCase())
    );

    setKeywordFilterModeState(hasRelatedKeywordsSelected ? "all" : "exact");
    setIsFilterModeInitialized(true);
  }, [searchConfiguration?.keywords, search, isFilterModeInitialized]);

  const [keywordsSelectLoadingStatus, setKeywordsSelectLoadingStatus] =
    useState<LoadingStatus>("idle");

  const [sort, setSort] = useState<{
    sortBy: string;
    sortDirection: SortType;
  }>({ sortBy: "totalVolume", sortDirection: "desc" });

  const [keywordsData, setKeywordsData] = useImmer<
    Search.KeywordsData | undefined
  >(defaultKeywordsData);

  const keywordsDataStatus = useMemo<LoadingStatus>(
    () => keywordsData?.status || "idle",
    [keywordsData?.status],
  );

  const previousKeywordsDataStatus = usePreviousState(keywordsDataStatus);

  const keywords = useMemo<Search.Keyword[]>(
    () => keywordsData?.keywords || [],
    [keywordsData?.keywords],
  );

  const isKeywordsLoading = useMemo<boolean>(
    () => keywordsDataStatus === "loading",
    [keywordsDataStatus],
  );

  const isKeywordsPending = useMemo<boolean>(
    () => !isObserverSet || searchStatus === "PENDING",
    [searchStatus, isObserverSet],
  );

  const filteredKeywordsByTab = useMemo<Search.Keyword[]>(() => {
    let baseKeywords = keywords;

    // Apply keyword type filter
    if (keywordFilterMode === "exact") {
      baseKeywords = baseKeywords.filter(
        ({ string }) => exactMatchRegex.test(string.toLowerCase()),
      );
    }

    if (keywordsSearch) {
      const formattedKeywordsSearch = removeExtraSpaces(
        keywordsSearch.toLowerCase(),
      );
      baseKeywords = baseKeywords.filter(({ string }) =>
        removeExtraSpaces(string.toLowerCase()).includes(
          formattedKeywordsSearch,
        ),
      );
    }

    switch (activeTab) {
      case "selected":
        return baseKeywords.filter(({ string }) =>
          Boolean(committedSelectedKeywords[string.toLowerCase()]),
        );

      case "unselected":
        return baseKeywords.filter(
          ({ string }) => !Boolean(committedSelectedKeywords[string.toLowerCase()]),
        );

      case "new":
        return baseKeywords.filter(({ isNew }) => isNew === true);

      case "duplicates":
        return getDuplicatedKeywords(baseKeywords);

      case "all":
      default:
        return baseKeywords;
    }
  }, [
    keywords,
    activeTab,
    committedSelectedKeywords,
    keywordsSearch,
    keywordFilterMode,
    search,
  ]);

  const searchedKeywords = useMemo<Search.Keyword[]>(() => {
    if (activeTab === "duplicates") {
      return filteredKeywordsByTab.filter(({ possibleDuplicateGroupId }) =>
        Boolean(possibleDuplicateGroupId),
      );
    }
    return filteredKeywordsByTab;
  }, [filteredKeywordsByTab, activeTab]);

  const isSelectAllDisabled = useMemo<boolean>(
    () => !searchedKeywords.length,
    [searchedKeywords.length],
  );

  const { groupedKeywords, groupedDuplicates } = useMemo(() => {
    if (!search) return { groupedKeywords: [], groupedDuplicates: [] };

    const groupedData = getGroupedKeywords(searchedKeywords, search);

    for (const [index, keyword] of groupedData.groupedDuplicates.entries())
      keyword.label = t("component.keywords_table.label.duplicate_group", {
        number: index + 1,
      });

    groupedData.groupedDuplicates = groupedData.groupedDuplicates.filter(
      (group) =>
        group.children?.some((child) => Boolean(committedSelectedKeywords[child.value?.toLowerCase()])),
    );

    return groupedData;
  }, [search, searchedKeywords, committedSelectedKeywords, t]);

  const sortChangeHandler = useCallback(
    (dataKey: string, sortDirection: SortType) => {
      setSort({ sortBy: dataKey, sortDirection });
    },
    [],
  );

  const keywordsTableData = useMemo<Search.FormattedKeyword[]>(() => {
    const data =
      activeTab === "duplicates" ? groupedDuplicates : groupedKeywords;

    if (!sort) return data;

    const collator = Intl.Collator();

    const sortedData = [...data].sort((a, b) => {
      let comparison = 0;

      switch (sort.sortBy) {
        case "string":
        case "value":
          comparison = collator.compare(a.label || "", b.label || "");
          break;
        case "related":
          const regexp = getExactMatchKeywordRegExp(search);
          const aIsExactMatch = regexp.test((a.label || "").toLowerCase());
          const bIsExactMatch = regexp.test((b.label || "").toLowerCase());
          const aIsRelated = !aIsExactMatch;
          const bIsRelated = !bIsExactMatch;

          if (aIsRelated === bIsRelated) {
            comparison = collator.compare(a.label || "", b.label || "");
          } else {
            comparison = aIsRelated ? 1 : -1;
          }
          break;
        case "totalVolume":
          comparison = (a.totalVolume || 0) - (b.totalVolume || 0);
          break;
        default:
          return 0;
      }

      return sort.sortDirection === "asc" ? comparison : -comparison;
    });

    return sortedData;
  }, [groupedDuplicates, groupedKeywords, sort, search, activeTab]);

  const keywordsSelectStatus = useMemo<SelectStatus>(() => {
    if (!searchedKeywords.length) return "unchecked";

    let [isAllSelected, isPartSelected] = [true, false];

    for (const { string: keyword } of searchedKeywords) {
      const normalizedKeyword = keyword.toLowerCase();
      const isKeywordSelected = Boolean(selectedKeywords[normalizedKeyword]);

      if (!isKeywordSelected) {
        isAllSelected = false;

        continue;
      }

      isPartSelected = true;
    }

    switch (true) {
      case isAllSelected:
        return "checked";
      case !isAllSelected && isPartSelected:
        return "partial";
      case !isAllSelected && !isPartSelected:
      default:
        return "unchecked";
    }
  }, [searchedKeywords, selectedKeywords]);

  const shouldKeywordsLoad = useMemo<boolean>(
    () =>
      keywordsDataStatus === "loading" &&
      defaultKeywordsData?.status === "loading",
    [defaultKeywordsData?.status, keywordsDataStatus],
  );

  const isKeywordsSelectionChanged = useMemo<boolean>(() => {
    const [defaultSelectedKeywordsArray, selectedKeywordsArray] = [
      Object.keys(defaultSelectedKeywords).sort(),
      Object.keys(selectedKeywords).sort(),
    ];

    return (
      JSON.stringify(defaultSelectedKeywordsArray) !==
      JSON.stringify(selectedKeywordsArray)
    );
  }, [defaultSelectedKeywords, selectedKeywords]);

  const isUpdateKeywordsDisabled = useMemo<boolean>(
    () => !Object.keys(selectedKeywords).length || !isKeywordsSelectionChanged,
    [selectedKeywords, isKeywordsSelectionChanged],
  );

  const selectKeywordsHandler = useCallback(
  (keywords: Keyword[]): void => {
    const newSelectedKeywords = { ...selectedKeywords };
    
    for (const keyword of keywords) {
      const normalizedKeyword = keyword.toLowerCase();
      if (newSelectedKeywords[normalizedKeyword]) {
        delete newSelectedKeywords[normalizedKeyword];
      } else {
        newSelectedKeywords[normalizedKeyword] = true;
      }
    }
    setSelectedKeywords(newSelectedKeywords);
  },
  [selectedKeywords],
);

  const updateKeywordsData = useCallback(
    (keywords: Search.Keyword[], status: LoadingStatus) =>
      setKeywordsData((draft) => {
        if (!draft) return;

        draft.status = status;
        draft.keywords = keywords;
      }),
    [setKeywordsData],
  );

  useEffect(() => {
    if (
      isKeywordsPending ||
      (keywordsDataStatus !== "idle" && !shouldKeywordsLoad)
    )
      return;

    updateKeywordsData([], "loading");

    getSearchKeywords(search.id)
      .then((keywords) => updateKeywordsData(keywords, "succeeded"))
      .catch(() => {
        updateKeywordsData([], "failed");

        showToastNotification({
          type: "error",
          text: t("common.error.server_error"),
        });
      });
  }, [
    t,
    search.id,
    isKeywordsPending,
    shouldKeywordsLoad,
    keywordsDataStatus,
    updateKeywordsData,
  ]);

  useEffect(() => {
    const hasKeywordsLoaded =
      previousKeywordsDataStatus === "loading" &&
      keywordsDataStatus === "succeeded";

    if (hasKeywordsLoaded) {
      setSelectedKeywords(defaultSelectedKeywords);
      setCommittedSelectedKeywords(defaultSelectedKeywords);
      
      const relatedKeywords: Record<Keyword, true> = {};
      for (const keyword in defaultSelectedKeywords) {
        const normalizedKeyword = keyword.toLowerCase();
        const isExactMatch = exactMatchRegex.test(keyword.toLowerCase());
        if (!isExactMatch) {
          relatedKeywords[normalizedKeyword] = true;
        }
      }
      if (Object.keys(relatedKeywords).length > 0) {
        dispatch(saveTemporaryRelatedKeywords({
          searchId: search.id,
          keywords: relatedKeywords,
        }));
      }
    }
  }, [defaultSelectedKeywords, keywordsDataStatus, previousKeywordsDataStatus, exactMatchRegex, search.id, dispatch]);

  const setActiveTabCommitted = (value: KeywordsTab) => {
    setCommittedSelectedKeywords(selectedKeywords);
    setActiveTab(value);
  };

  const selectAllKeywordsHandler = (): void => {
    if (keywordsSelectStatus === "checked")
      return selectKeywordsHandler(
        searchedKeywords.map(({ string }) => string),
      );

    const unselectedKeywords = new Set<Keyword>();

    for (const { string: keyword } of searchedKeywords) {
      const normalizedKeyword = keyword.toLowerCase();
      const isKeywordSelected = Boolean(selectedKeywords[normalizedKeyword]);

      if (!isKeywordSelected) unselectedKeywords.add(keyword);
    }

    return selectKeywordsHandler([...unselectedKeywords]);
  };

  const selectRecommendedKeywordsHandler = async (): Promise<void> => {
    if (!search) return;

    try {
      setKeywordsSelectLoadingStatus("loading");

      const keywords = await getSearchSelectedKeywords(search.id);

      const selectedKeywords = new Map<Keyword, true>();

      for (const keyword of keywords) selectedKeywords.set(keyword, true);

      setSelectedKeywords(Object.fromEntries(selectedKeywords));

      setAutoSelectedKeywords(Object.fromEntries(selectedKeywords));

      setKeywordsSelectLoadingStatus("succeeded");
    } catch (error) {
      console.error(error);

      setKeywordsSelectLoadingStatus("failed");

      showToastNotification({
        type: "error",
        text: t("common.error.server_error"),
      });
    }
  };

  const setKeywordFilterMode = useCallback((mode: "exact" | "all") => {
    if (mode === "all") {
      const updatedSelectedKeywords = { ...selectedKeywords };
      
      const relatedKeywordsToRestore = hasTemporaryKeywords
        ? Object.keys(temporaryRelatedKeywords).map(k => k.toLowerCase())
        : (searchConfiguration?.relatedKeywords?.map(k => k.toLowerCase()) || []);
      
      for (const keyword of relatedKeywordsToRestore) {
        const normalizedKeyword = keyword.toLowerCase();
        updatedSelectedKeywords[normalizedKeyword] = true;
      }
      
      setSelectedKeywords(updatedSelectedKeywords);
    } else {
      const relatedKeywordsToSave: Record<Keyword, true> = {};
      const updatedSelectedKeywords: Record<Keyword, true> = {};

      for (const keyword in selectedKeywords) {
        const isExactMatch = exactMatchRegex.test(keyword.toLowerCase());
        if (isExactMatch) {
          updatedSelectedKeywords[keyword] = true;
        } else {
          relatedKeywordsToSave[keyword] = true;
        }
      }
      
      dispatch(saveTemporaryRelatedKeywords({
        searchId: search.id,
        keywords: relatedKeywordsToSave,
      }));
      setSelectedKeywords(updatedSelectedKeywords);
    }
    
    setKeywordFilterModeState(mode);
  }, [selectedKeywords, temporaryRelatedKeywords, hasTemporaryKeywords, searchConfiguration?.relatedKeywords, exactMatchRegex, search.id, dispatch]);

  const saveSelectedKeywordsHandler = async (): Promise<void> => {
    if (isUpdateKeywordsDisabled) return;

    const oldKeywords = searchConfiguration?.keywords || [];
    
    const keywordCaseMap = new Map<string, string>();
    for (const { string: keyword } of keywords) {
      keywordCaseMap.set(keyword.toLowerCase(), keyword);
    }
    
    const newKeywords = Object.keys(selectedKeywords).map(normalizedKey => {
      return keywordCaseMap.get(normalizedKey) || normalizedKey;
    });
    
    const keywordChanges = detectKeywordChanges(oldKeywords, newKeywords);

    const handleRecalculate = async (
      mode: "full" | "future",
    ): Promise<void> => {
      try {
        let updatedMetadata: Search.KeywordMetadata[] = [];

        if (mode === "future") {
          const effectiveFrom = latestKeywordsDate || "";

          if (!effectiveFrom) {
            showToastNotification({
              type: "error",
              text: "Unable to determine latest keywords date",
            });
            throw new Error("No effectiveFrom date available");
          }

          const existingMetadata = searchConfiguration?.keywordsMetadata || [];
          const newMetadataEntries: Search.KeywordMetadata[] = [
            ...keywordChanges.added.map((keyword) => ({
              keyword,
              effectiveFrom,
              changeType: "added" as const,
            })),
            ...keywordChanges.removed.map((keyword) => ({
              keyword,
              effectiveFrom,
              changeType: "removed" as const,
            })),
          ];

          const changedKeywords = new Set([
            ...keywordChanges.added,
            ...keywordChanges.removed,
          ]);
          const filteredExistingMetadata = existingMetadata.filter(
            (meta) => !changedKeywords.has(meta.keyword),
          );
          updatedMetadata = [
            ...filteredExistingMetadata,
            ...newMetadataEntries,
          ];
        }

        await dispatch(
          updateSearchConfiguration({
            id: search.id,
            changes: {
              keywords: newKeywords,
              keywordsMetadata: updatedMetadata,
            },
          }),
        ).unwrap();

        const trackersCollectionsPayload = trackersCollections.map(
          ({ id }) => ({
            id,
            changes: {},
          }),
        );

        const trackersPayload = trackers.map(({ id }) => ({
          id,
          changes: {},
        }));

        const searchPayload: Store.UpdateEntity<Search.Data> = {
          id: search.id,
          changes: { status: "READY" },
        };

        await Promise.all([
          dispatch(updateSearch(searchPayload)),
          dispatch(updateTrackers(trackersPayload)),
          dispatch(
            updateTrackersCollections(trackersCollectionsPayload),
          ).unwrap(),
        ]);

        showToastNotification({
          type: "success",
          text: t("component.keywords_table.status.success.keywords_updated"),
        });

        // Clear temporary related keywords after successful save
        dispatch(clearTemporaryRelatedKeywords(search.id));

        closeSidebarHandler();
      } catch (error) {
        console.error(error);
        showToastNotification({
          type: "error",
          text: t("common.error.server_error"),
        });
        throw error;
      }
    };

    return setModal(
      "recalculation-confirm",
      <RecalculationConfirmModal
        search={search}
        onRecalculate={handleRecalculate}
        onCancel={() => closeModal("recalculation-confirm")}
      />,
    );
  };

  const tracker = useSelector((state: Store.RootState) =>
    selectTrackerById(state, trackerId!),
  );

  const trackerSearches = useSelector((state: Store.RootState) =>
    selectSearchesByTrackerId(state, trackerId!),
  );

  const { selectSearch: localSelectSearch } = useTrackerSearches({
    trackerId: trackerId!,
    searchIds: tracker?.searchIds || [],
    selectedSearches: trackerSearches,
  });

  const selectSearch = selectSearchHandler || localSelectSearch;

  const createCopySearchHandler = async (): Promise<void> => {
    if (isUpdateKeywordsDisabled) return;

    try {
      const {
        locationId,
        subject,
        languageId,
        description,
        keywordsDataSource,
        keywordsSelectionMethod,
        isDuplicate,
        initialSearchId,
        status,
      } = search;

      const newSearch = {
        id: generateDocId(),
        locationId,
        subject,
        languageId,
        description,
        status,
        keywordsDataSource,
        keywordsSelectionMethod,
        isDuplicate,
        initialSearchId,
        keywords: Object.keys(selectedKeywords),
      };

      const createdSearch = await dispatch(createSearch(newSearch)).unwrap();

      selectSearch(createdSearch, () => {
        showToastNotification({
          type: "success",
          text: t("component.keywords_table.status.success.search_created"),
        });

        closeSidebarHandler();
      });
    } catch (error) {
      console.error(error);
      showToastNotification({
        type: "error",
        text: t("common.error.server_error"),
      });
    }
  };

  return (
    <div className={styles.wrapper}>
      <SelectedSearch search={search} />
      <div className={styles.keywordsTable}>
        <KeywordsControl
          keywordsSearch={keywordsSearch}
          selectedKeywords={selectedKeywords}
          isKeywordsLoading={isKeywordsLoading}
          setKeywordsSearch={setKeywordsSearch}
          isKeywordsPending={isKeywordsPending}
          autoSelectedKeywords={autoSelectedKeywords}
          activeTab={activeTab}
          setActiveTab={(value) => setActiveTabCommitted(value as KeywordsTab)}
          keywordsSelectLoadingStatus={keywordsSelectLoadingStatus}
          selectRecommendedKeywordsHandler={selectRecommendedKeywordsHandler}
          keywordFilterMode={keywordFilterMode}
          setKeywordFilterMode={setKeywordFilterMode}
        />
        <KeywordsTable
          searchId={search.id}
          search={search}
          data={keywordsTableData}
          keywords={keywords}
          searchedKeywords={searchedKeywords}
          selectedKeywords={selectedKeywords}
          isKeywordsLoading={isKeywordsLoading}
          isKeywordsPending={isKeywordsPending}
          isSelectAllDisabled={isSelectAllDisabled}
          keywordsSelectStatus={keywordsSelectStatus}
          isDuplicatedKeywordsShown={activeTab === "duplicates"}
          selectAllKeywordsHandler={selectAllKeywordsHandler}
          selectKeywordsHandler={selectKeywordsHandler}
          sort={{
            sortBy: sort.sortBy,
            sortDirection: sort.sortDirection,
            sortChangeHandler,
          }}
        />
        <div className={styles.buttonsWrapper}>
          <Button
            buttonStyle="outlined"
            className={styles.button}
            onClick={closeSidebarHandler}
          >
            {t("component.keywords_table.button.cancel")}
          </Button>
          {allowChangeTracker ? (
            <Button
              className={styles.button}
              onClick={saveSelectedKeywordsHandler}
              disabled={isUpdateKeywordsDisabled}
            >
              {t("component.keywords_table.button.submit")}
            </Button>
          ) : (
            <Button
              className={styles.button}
              onClick={createCopySearchHandler}
              disabled={isUpdateKeywordsDisabled}
            >
              {t("component.keywords_table.button.submit_copy")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
